/**
 * Phase 9A — Concierge Admin (minimal).
 *
 * The smallest possible admin surface needed to move a Concierge event
 * from "received" to "Brief ready" for the customer. No notes, no
 * reassign, no bridge UI, no outreach, no completion, no analytics.
 *
 * Layout:
 *   * Pending queue list (Concierge events not cancelled/completed)
 *   * Click a row → drawer with intake preview + claim + brief actions
 *
 * Backed entirely by existing Phase 5/8 admin endpoints + the new 9A
 * queue endpoint:
 *   * GET    /admin/concierge/queue
 *   * POST   /admin/concierge/workspace/{event_id}/claim
 *   * POST   /admin/concierge/workspace/{event_id}/brief/generate
 *   * POST   /admin/concierge/brief/{brief_id}/seal
 *   * GET    /admin/concierge/brief/{brief_id}/render/html  (preview)
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronDown, ChevronLeft, ChevronUp, FileText, RefreshCw, Sparkles, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { isConciergeManualMode } from '../../components/operations/manualModeFlag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// B-EXP-6.19 Defect 1 — mirrors CaseWorkspacePage.STATUS_LABELS so a case
// change made in the workspace shows the same badge back here on the
// queue. The queue endpoint returns `row.status` sourced from
// `db.events.status` (derive_status in concierge_workspace.py), which is
// the single authoritative operational field.
const STATUS_LABELS = {
  new: 'New',
  claimed: 'Claimed',
  reviewing: 'Reviewing',
  researching: 'Researching',
  waiting_customer: 'Waiting on customer',
  waiting_vendor: 'Waiting on vendor',
  recommendations_sent: 'Recommendations sent',
  followup_needed: 'Follow-up needed',
  closed: 'Closed',
};

const STATUS_CHIP_CLASSES = {
  new: 'bg-amber-50 text-amber-700 border-amber-200',
  claimed: 'bg-violet-50 text-violet-700 border-violet-200',
  reviewing: 'bg-violet-50 text-violet-700 border-violet-200',
  researching: 'bg-violet-50 text-violet-700 border-violet-200',
  waiting_customer: 'bg-amber-50 text-amber-800 border-amber-200',
  waiting_vendor: 'bg-amber-50 text-amber-800 border-amber-200',
  recommendations_sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  followup_needed: 'bg-rose-50 text-rose-700 border-rose-200',
  closed: 'bg-stone-100 text-stone-600 border-stone-200',
};

function statusChipClass(status) {
  return STATUS_CHIP_CLASSES[status] || STATUS_CHIP_CLASSES.new;
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

export default function ConciergeAdminPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null); // queue row currently viewed in drawer
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [latestDraft, setLatestDraft] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  // Phase A — Manual Mode collapse state for the Hybrid Draft Tools
  // disclosure. Default collapsed so the back-office Brief plumbing
  // does not dominate the drawer when the customer-facing surface
  // (Manual Mode) does not show the Brief at all.
  const [hybridToolsOpen, setHybridToolsOpen] = useState(false);

  const fetchQueue = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/concierge/queue`, { headers: authHeaders() });
      setRows(res.data?.rows || []);
    } catch (e) {
      toast.error('Could not load Concierge queue');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const loadDrawerState = useCallback(async (row) => {
    setSelected(row);
    setLatestDraft(null);
    setPreviewHtml(null);
    if (!row?.event_id) return;
    setDrawerLoading(true);
    try {
      const briefs = await axios.get(
        `${API}/admin/concierge/workspace/${row.event_id}/briefs`,
        { headers: authHeaders() },
      );
      const list = briefs.data || [];
      // Most recent draft (status="draft") is the candidate for sealing.
      const draft = list.find((b) => b.status === 'draft');
      setLatestDraft(draft || null);
    } catch (_) {
      // best-effort — drawer still usable
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  // Phase A.5 — Admin Navigation Stabilization (permanent).
  // /admin/concierge?event=<event_id> is the supported deep-link
  // pattern for opening a specific Concierge request. After the
  // queue loads, if the URL points at a specific event AND that
  // event is present in the queue, auto-open its workspace drawer.
  // The query param is consumed (cleared) once the drawer opens so
  // the URL stays clean during the rest of the admin's session and
  // re-clicking the same Attention card re-triggers this effect.
  //
  // NOTE: this effect MUST be declared AFTER `loadDrawerState` so the
  // dependency array does not trigger a Temporal Dead Zone error on
  // first render. Do not reorder these blocks.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkEventId = searchParams.get('event');

  useEffect(() => {
    if (!deepLinkEventId) return;
    if (loading) return; // wait for queue to finish loading
    const target = rows.find((r) => r.event_id === deepLinkEventId);
    if (target) {
      // B-EXP-1 — queue navigation now lands on the full case workspace
      // page instead of opening the legacy drawer. Phase A.5's deep-link
      // contract (?event=<id>) is preserved as an automatic redirect.
      setSearchParams({}, { replace: true });
      navigate(`/admin/concierge/cases/${deepLinkEventId}`, { replace: true });
    } else {
      // Event isn't in the active queue (e.g. completed). Calm
      // info-level toast; do NOT blank-state the page.
      toast.message('Event not in the active Concierge queue');
      setSearchParams({}, { replace: true });
    }
  }, [deepLinkEventId, loading, rows, navigate, setSearchParams]);

  const closeDrawer = () => {
    setSelected(null);
    setLatestDraft(null);
    setPreviewHtml(null);
  };

  const claim = async () => {
    if (!selected?.event_id) return;
    try {
      await axios.post(
        `${API}/admin/concierge/workspace/${selected.event_id}/claim`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Event claimed');
      await fetchQueue();
      // refresh drawer row data
      const fresh = (await axios.get(`${API}/admin/concierge/queue`, { headers: authHeaders() })).data?.rows || [];
      const updated = fresh.find((r) => r.event_id === selected.event_id);
      if (updated) await loadDrawerState(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Claim failed');
    }
  };

  const generateDraft = async () => {
    if (!selected?.event_id) return;
    try {
      const res = await axios.post(
        `${API}/admin/concierge/workspace/${selected.event_id}/brief/generate`,
        {},
        { headers: authHeaders() },
      );
      setLatestDraft(res.data || null);
      toast.success('Draft generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Draft generation failed');
    }
  };

  const sealBrief = async () => {
    if (!latestDraft?.id) return;
    try {
      await axios.post(
        `${API}/admin/concierge/brief/${latestDraft.id}/seal`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Brief sealed — ready for customer');
      await fetchQueue();
      const fresh = (await axios.get(`${API}/admin/concierge/queue`, { headers: authHeaders() })).data?.rows || [];
      const updated = fresh.find((r) => r.event_id === selected.event_id);
      if (updated) await loadDrawerState(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seal failed');
    }
  };

  const previewBrief = async () => {
    const briefId = latestDraft?.id || selected?.sealed_brief?.id;
    if (!briefId) return;
    try {
      const res = await axios.get(
        `${API}/admin/concierge/brief/${briefId}/render/html`,
        { headers: authHeaders(), responseType: 'text', transformResponse: [(d) => d] },
      );
      setPreviewHtml(res.data || '');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-5 pt-6 pb-12">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard')}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            data-testid="concierge-admin-back"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden />
            Admin
          </button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchQueue}
            disabled={refreshing}
            data-testid="concierge-admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-1">Concierge queue</h1>
        <p className="text-sm text-gray-500 mb-6">
          Active Concierge events waiting for a brief.
        </p>

        {loading ? (
          <div className="space-y-2" data-testid="concierge-admin-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 bg-white border border-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div
            className="bg-white border border-gray-100 rounded p-6 text-sm text-gray-500"
            data-testid="concierge-admin-empty"
          >
            No active Concierge events.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="concierge-admin-queue">
            {rows.map((row) => (
              <li key={row.event_id}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/concierge/cases/${row.event_id}`)}
                  className="w-full text-left bg-white border border-gray-100 rounded p-4 hover:border-violet-200 transition-colors"
                  data-testid={`concierge-admin-row-${row.event_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {row.event_type || 'Event'} · {row.customer_email || 'customer'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Submitted {formatTime(row.created_at)}
                      </p>
                      {row.intake_preview && (
                        <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                          {row.intake_preview}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      {/* B-EXP-6.19 Defect 1 — the queue card badge now mirrors
                          the authoritative operational status persisted on
                          the event row (source: derive_status() in
                          concierge_workspace.py). This is the SAME field the
                          Case Workspace status control reads and writes, so
                          any status change made in the workspace shows here
                          the next time the queue is loaded. "Brief sealed" is
                          kept as a secondary chip so staff can still see
                          publish progress at a glance. */}
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full border ${statusChipClass(row.status)}`}
                        data-testid={`concierge-admin-status-${row.event_id}`}
                        data-status={row.status || 'new'}
                      >
                        {STATUS_LABELS[row.status] || 'New'}
                      </span>
                      {row.sealed_brief ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                          data-testid={`concierge-admin-sealed-${row.event_id}`}
                        >
                          <CheckCircle2 className="w-3 h-3" aria-hidden />
                          Brief sealed
                        </span>
                      ) : null}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-40 bg-black/40 flex justify-end"
          onClick={closeDrawer}
          data-testid="concierge-admin-drawer"
        >
          <div
            className="bg-white w-full max-w-xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {selected.event_type || 'Event'} · {selected.customer_email}
                </p>
                <p className="text-xs text-gray-500">Submitted {formatTime(selected.created_at)}</p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="text-gray-400 hover:text-gray-700"
                data-testid="concierge-admin-drawer-close"
              >
                <X className="w-5 h-5" aria-hidden />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Intake */}
              <section>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Intake</p>
                <p
                  className="text-sm text-gray-700 whitespace-pre-wrap"
                  data-testid="concierge-admin-drawer-intake"
                >
                  {selected.intake_preview || '(no description provided)'}
                </p>
              </section>

              {/* Claim */}
              <section>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Claim</p>
                {selected.claim?.worker_id ? (
                  <p className="text-sm text-gray-600" data-testid="concierge-admin-drawer-claim-state">
                    Claimed at {formatTime(selected.claim.claimed_at)}.
                  </p>
                ) : (
                  <Button
                    onClick={claim}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="concierge-admin-drawer-claim"
                  >
                    Claim event
                  </Button>
                )}
              </section>

              {/* Brief — Phase A fork (ADR-CONCIERGE-PHASE-A).
                  Manual Mode (production reality): the customer never
                  sees an in-app Brief, so the existing Generate /
                  Preview / Seal tree is wrapped in a collapsed
                  "Hybrid Draft Tools" disclosure under a calm
                  Manual-Mode status line. Hybrid Mode (Phase D)
                  bypasses the disclosure entirely and renders the
                  original tree verbatim — zero admin keystroke
                  change for that mode. */}
              {(() => {
                const manualMode = isConciergeManualMode();

                // Renders the existing 4-way conditional tree byte-for-byte
                // (Sealed / Draft / Claimed-no-draft / Unclaimed). All
                // click handlers and data-testids are preserved.
                const briefToolsTree = (
                  drawerLoading ? (
                    <p className="text-sm text-gray-400">Loading brief state…</p>
                  ) : selected.sealed_brief ? (
                    <div className="space-y-2">
                      <p className="text-sm text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" aria-hidden />
                        Sealed (revision {selected.sealed_brief.revision || 1}) · {formatTime(selected.sealed_brief.sealed_at)}
                      </p>
                      <Button
                        variant="outline"
                        onClick={previewBrief}
                        data-testid="concierge-admin-drawer-preview"
                      >
                        <FileText className="w-4 h-4 mr-1.5" />
                        Preview brief
                      </Button>
                    </div>
                  ) : latestDraft ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700">
                        Draft revision {latestDraft.revision || 1} — review before sealing.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={previewBrief}
                          data-testid="concierge-admin-drawer-preview"
                        >
                          <FileText className="w-4 h-4 mr-1.5" />
                          Preview
                        </Button>
                        <Button
                          onClick={sealBrief}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="concierge-admin-drawer-seal"
                        >
                          <Sparkles className="w-4 h-4 mr-1.5" />
                          Seal brief
                        </Button>
                      </div>
                    </div>
                  ) : selected.claim?.worker_id ? (
                    <Button
                      onClick={generateDraft}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="concierge-admin-drawer-generate"
                    >
                      Generate Brief draft
                    </Button>
                  ) : (
                    <p className="text-sm text-gray-500">Claim the event to generate a brief.</p>
                  )
                );

                const previewPanel = previewHtml ? (
                  <section data-testid="concierge-admin-drawer-preview-panel">
                    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Preview</p>
                    <iframe
                      title="Brief preview"
                      srcDoc={previewHtml}
                      sandbox="allow-same-origin"
                      className="w-full min-h-[60vh] border border-gray-100 rounded"
                    />
                  </section>
                ) : null;

                // ── Hybrid Mode (Phase D) — original layout preserved ──
                if (!manualMode) {
                  return (
                    <>
                      <section>
                        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Brief</p>
                        {briefToolsTree}
                      </section>
                      {previewPanel}
                    </>
                  );
                }

                // ── Manual Mode — status line + collapsed disclosure ──
                const manualStatusText = selected.sealed_brief
                  ? `An in-app Brief exists (sealed ${formatTime(selected.sealed_brief.sealed_at)}, revision ${selected.sealed_brief.revision || 1}) but is NOT shown to the customer in Manual Mode. Customer outreach happens by email.`
                  : latestDraft
                    ? `A Brief draft exists (revision ${latestDraft.revision || 1}) but is NOT shown to the customer in Manual Mode. Customer outreach happens by email.`
                    : selected.claim?.worker_id
                      ? 'Event is claimed. No Brief artifact yet. Customer outreach happens by email.'
                      : 'Event is unclaimed. Claim it above before using the Hybrid Draft Tools below.';

                return (
                  <>
                    <section data-testid="concierge-admin-manual-mode-status">
                      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                        Customer-facing surface
                      </p>
                      <div className="rounded-lg bg-violet-50/60 border border-violet-100 p-3 space-y-1">
                        <p className="text-sm font-medium text-gray-900 leading-snug">
                          Manual Mode — calm acknowledgement + email guidance only
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {manualStatusText}
                        </p>
                      </div>
                    </section>

                    <section>
                      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                        Brief artifact (back-office only)
                      </p>
                      <button
                        type="button"
                        onClick={() => setHybridToolsOpen(v => !v)}
                        aria-expanded={hybridToolsOpen}
                        aria-controls="concierge-admin-hybrid-tools-panel"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-left"
                        data-testid="concierge-admin-hybrid-tools-toggle"
                      >
                        <span className="text-sm font-medium text-gray-800">
                          Hybrid Draft Tools
                        </span>
                        {hybridToolsOpen
                          ? <ChevronUp className="w-4 h-4 text-gray-500" aria-hidden />
                          : <ChevronDown className="w-4 h-4 text-gray-500" aria-hidden />}
                      </button>

                      {hybridToolsOpen && (
                        <div
                          id="concierge-admin-hybrid-tools-panel"
                          data-testid="concierge-admin-hybrid-tools-panel"
                          className="mt-3 pl-3 border-l-2 border-gray-100 space-y-3"
                        >
                          <p className="text-xs text-gray-500 leading-relaxed">
                            These tools generate and seal an in-app Brief artifact for the back office. The customer-facing Manual Mode surface does NOT show the Brief in Phase A.
                          </p>
                          {briefToolsTree}
                          {previewPanel}
                        </div>
                      )}
                    </section>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
